import { applicationFieldShowValidationStatus } from 'common/actions/application'
import { lifeInsuranceApplicationEmailFieldSelector } from 'embed/selectors/application'
import {
  fetchRateTablesSuccess,
  lifeInsuranceOfferSelected,
  lifeInsuranceReportProductSubType,
  ratesFilterReplaced,
  stepSelected,
  updateLead,
} from 'common/actions'
import {
  FieldData,
  FieldKey,
  LifeInsuranceCoverageTier,
  LifeInsuranceResultsView,
  MultiProductDispatch,
  RatesFilter,
  RateTable,
} from 'embed/constants'
import { captureGetRateTablesError, captureRefreshRateTablesError } from 'embed/constants/errors'
import { filteredRatesSelector } from 'embed/selectors'
import {
  configCompanyUuidSelector,
  configShouldUseCustomBackgroundSelector,
  isLeapLifeBrandedSelector,
} from 'embed/selectors/config'
import { loadingErrorSelector, loadingSelector } from 'embed/selectors/loading'
import {
  lifeInsuranceRatesLeadUuidSelector,
  lifeInsuranceRatesOffersSelector,
  lifeInsuranceRatesUuidSelector,
  lifeInsuranceRecommendedViewRatesOffersSelector,
} from 'embed/selectors/rates'
import Api, { ApiResponse } from 'common/services/Api'
import Session from 'common/services/Session'
import * as actions from 'embed/actions'
import { LifeInsuranceOffer, LifeInsuranceProductSubType } from 'embed/constants/models'
import {
  uiLifeInsuranceCoverageAmountSelector,
  uiLifeInsuranceCoverageDurationSelector,
  uiLifeInsuranceResultsViewSelector,
} from 'embed/selectors/ui'
import { ConfigKeys, LifeInsuranceStep, Product, ProductResponseKeys, ProductStep } from 'even-ts-static'
import * as React from 'react'
import { connect } from 'react-redux'
import { LifeInsuranceResults } from './index'

import ErrorIndicator from 'embed/components/ErrorIndicator'
import LoadingIndicator, { BRAND_COLOR } from 'embed/components/LoadingIndicator'
import Parent from 'common/services/Parent'
import { MultiProductState } from 'embed/reducers'
import { LifeInsuranceSimplifiedOffers, Original, Variant } from 'ui/multi-product/experiments'
import { UnifiedResults } from '../UnifiedResults'

interface DispatchProps {
  dispatchAppendRateTableUuidToParentUrl: (uuid: string) => void
  dispatchRatesFilterChanged: (RatesFilter, LifeInsuranceCoverageTier) => void
  dispatchSetResultsView: (LifeInsuranceResultsView) => void
  dispatchUpdateLead: () => void
  dispatchReportProductSubType: (productSubType: LifeInsuranceProductSubType) => void
  dispatchLifeInsuranceOfferLoaded: () => void
  dispatchStepSelected: (product: Product, step: ProductStep) => void
  dispatchApplicationFieldShowValidationStatus: (fieldKey: FieldKey, show: boolean) => void
  dispatchFetchRateTablesSuccess: (product: Product, data) => void
  dispatchCoverageAmount: (LifeInsuranceCoverageAmount) => void
  dispatchCoverageDuration: (LifeInsuranceCoverageDuration) => void
  onOfferSelected: (offer: LifeInsuranceOffer) => void
}

interface StoreProps {
  initialSelectedOffer?: LifeInsuranceOffer
  allLifeInsuranceOffers: LifeInsuranceOffer[]
  browseViewLifeInsuranceOffers: LifeInsuranceOffer[]
  configuredStep: LifeInsuranceStep
  companyUuid: string
  globalError: boolean
  loading: boolean
  isLeapLifeBranded: boolean
  isOfferLoaded: boolean
  offerUuid?: string
  rateTableLeadUuid: string
  rateTableUuid: string
  recommendedViewLifeInsuranceOffers: LifeInsuranceOffer[]
  resultsView: LifeInsuranceResultsView
  selectedOffer?: LifeInsuranceOffer
  applicationEmail?: FieldData
  coverageAmount: number
  coverageDuration: number
  enableCustomBackground: boolean
}

type Props = DispatchProps & StoreProps

const UnconnectedLifeInsuranceResultsContainer: React.FC<Props> = (props) => {
  const {
    allLifeInsuranceOffers,
    configuredStep,
    globalError,
    loading,
    isOfferLoaded,
    offerUuid,
    dispatchAppendRateTableUuidToParentUrl,
    dispatchReportProductSubType,
    dispatchSetResultsView,
    dispatchUpdateLead,
    applicationEmail,
    dispatchStepSelected,
    dispatchLifeInsuranceOfferLoaded,
    dispatchFetchRateTablesSuccess,
    dispatchCoverageAmount,
    dispatchCoverageDuration,
    rateTableLeadUuid,
    rateTableUuid,
    resultsView,
    coverageAmount,
    coverageDuration,
  } = props
  const [requestACallDisplayed, setRequestACallDisplayed] = React.useState(false)
  const [loadingCustomizedOffer, setLoadingCustomizedOffer] = React.useState(false)
  const initialOffer = allLifeInsuranceOffers[0]
  const notCovAmountOrNotCovDuration = !coverageAmount || !coverageDuration
  const [viewOriginalLayout, setViewOriginalLayout] = React.useState(false)
  let requestStartTime: Date

  const onViewOriginalLayout = () => {
    setLoadingCustomizedOffer(true)
    setViewOriginalLayout(true)
    Parent.scrollToFrame()
    setTimeout(() => setLoadingCustomizedOffer(false), 1000)
  }

  const fetchRateTable = async (data: { carrierUuid?: string; duration?: number; policyAmount?: number }) => {
    setLoadingCustomizedOffer(true)
    requestStartTime = new Date()
    Api.post(`/lifeInsurance/rateTables/${rateTableUuid}/customize`, {
      lifeInsuranceInformation: data,
      uuid: rateTableLeadUuid,
    })
      .then(handleResponse)
      .catch((error) => {
        captureGetRateTablesError(Product.LifeInsurance, error)
      })
  }

  const refreshRateTable = async (rateTableResultUuid) => {
    try {
      const response = await Api.get(`/originator/rateTables/${rateTableResultUuid}`)
      handleResponse(response)
    } catch (error) {
      captureRefreshRateTablesError(Product.LifeInsurance, rateTableResultUuid, error)
    }
  }

  const handleResponse = (response: ApiResponse<RateTable>) => {
    const rateTableResultUuid = response.data.uuid
    if (!response.data.pendingResponses?.length && !response.data.pendingOriginators?.length) {
      dispatchFetchRateTablesSuccess(Product.LifeInsurance, response.data)
      setLoadingCustomizedOffer(false)
    } else {
      // Poll every 100ms for 4 seconds, then poll at 1s
      const pollRate = requestStartTime.getTime() + 4000 > new Date().getTime() ? 100 : 1000
      setTimeout(() => refreshRateTable(rateTableResultUuid), pollRate)
    }
  }

  const handleDropdownChange = () => {
    dispatchSetResultsView(LifeInsuranceResultsView.Recommended)
    if (!requestACallDisplayed) {
      setRequestACallDisplayed(true)
    }
  }

  const onCoverageAndTermChange = (policyAmount: number, duration: number) => {
    dispatchCoverageAmount(policyAmount)
    dispatchCoverageDuration(duration)
    fetchRateTable({
      duration,
      policyAmount,
    }).catch((error) => {
      captureGetRateTablesError(Product.LifeInsurance, error)
    })
  }

  const onCoverageAmountChange = (value: number) => {
    dispatchCoverageAmount(value)
    handleDropdownChange()
    fetchRateTable({
      policyAmount: value,
      duration: coverageDuration,
    }).catch((error) => {
      captureGetRateTablesError(Product.LifeInsurance, error)
    })
  }

  const onDurationChange = (value: number) => {
    dispatchCoverageDuration(value)
    handleDropdownChange()
    fetchRateTable({
      policyAmount: coverageAmount,
      duration: value,
    }).catch((error) => {
      captureGetRateTablesError(Product.LifeInsurance, error)
    })
  }

  React.useEffect(() => {
    dispatchAppendRateTableUuidToParentUrl(rateTableUuid)
  }, [])

  React.useEffect(() => {
    if (notCovAmountOrNotCovDuration) {
      for (const offer of allLifeInsuranceOffers) {
        if (offer.productSubType === LifeInsuranceProductSubType.TermLife) {
          dispatchCoverageAmount(offer.details.policyAmount)
          dispatchCoverageDuration(offer.details.termLength)
          break
        }
      }
    }
  }, [allLifeInsuranceOffers, dispatchCoverageAmount, dispatchCoverageDuration, notCovAmountOrNotCovDuration])

  React.useEffect(() => {
    if (offerUuid && !isOfferLoaded && initialOffer) {
      dispatchStepSelected(Product.LifeInsurance, LifeInsuranceStep.PersonalInfo)
      dispatchLifeInsuranceOfferLoaded()
    }
  }, [isOfferLoaded, initialOffer, offerUuid, dispatchStepSelected, dispatchLifeInsuranceOfferLoaded])

  React.useEffect(() => {
    if (rateTableUuid) {
      dispatchAppendRateTableUuidToParentUrl(rateTableUuid)
      Parent.publishMessage('rate_table_uuid', { rateTableUuid })
    }
  }, [rateTableUuid])

  React.useEffect(() => {
    if (configuredStep === LifeInsuranceStep.Results) {
      dispatchAppendRateTableUuidToParentUrl(rateTableUuid)
      const { leadUuidHasChanged } = Session.forceOverrideLeadUuid(rateTableLeadUuid)
      if (leadUuidHasChanged) {
        // tslint:disable-next-line:no-floating-promises
        dispatchUpdateLead()
      }
    }
  }, [rateTableLeadUuid])

  const productSubType = initialOffer?.productSubType
  React.useEffect(() => {
    if (productSubType !== undefined) {
      dispatchReportProductSubType(productSubType)
    }
  }, [productSubType])

  const onToggleResultsView = () => {
    dispatchSetResultsView(
      resultsView === LifeInsuranceResultsView.Recommended
        ? LifeInsuranceResultsView.Browse
        : LifeInsuranceResultsView.Recommended,
    )

    Parent.scrollToFrame()
  }

  const offerNotLoaded = props.offerUuid && !isOfferLoaded
  if (globalError) {
    return <ErrorIndicator />
  } else if (loading || offerNotLoaded) {
    return (
      <LoadingIndicator
        brandColor={BRAND_COLOR.TRUE}
        loadingLabel="Finding Life Insurance Offers"
        showPendingOriginators={true}
        product={Product.LifeInsurance}
      />
    )
  } else {
    return (
      <React.Fragment>
        <Original>
          <LifeInsuranceResults
            {...props}
            onToggleResultsView={onToggleResultsView}
            applicationEmail={applicationEmail}
            onCoverageAmountChange={onCoverageAmountChange}
            onDurationChange={onDurationChange}
            loadingCustomizedOffer={loadingCustomizedOffer}
            requestACallDisplayed={requestACallDisplayed}
          />
        </Original>
        <Variant
          experiment={LifeInsuranceSimplifiedOffers}
          variant={LifeInsuranceSimplifiedOffers.variants.UpdatedRatesDisplay}
        >
          <UnifiedResults
            {...props}
            offers={props.allLifeInsuranceOffers}
            onCoverageAndTermChange={onCoverageAndTermChange}
            loadingCustomizedOffer={loadingCustomizedOffer}
            onCoverageAmountChange={onCoverageAmountChange}
            onDurationChange={onDurationChange}
            dispatchCoverageAmount={dispatchCoverageAmount}
            dispatchCoverageDuration={dispatchCoverageDuration}
            onViewMoreOffers={onViewOriginalLayout}
            onToggleResultsView={onToggleResultsView}
            applicationEmail={applicationEmail}
            requestACallDisplayed={requestACallDisplayed}
            showOriginalLayout={viewOriginalLayout}
          />
        </Variant>
      </React.Fragment>
    )
  }
}

export const mapStateToProps = (state: MultiProductState): StoreProps => {
  const allLifeInsuranceOffers: LifeInsuranceOffer[] = lifeInsuranceRatesOffersSelector(state)
  const browseViewLifeInsuranceOffers: LifeInsuranceOffer[] = filteredRatesSelector(state, {
    offersPath: `${Product.LifeInsurance}.${ProductResponseKeys[Product.LifeInsurance]}Offers`,
    product: Product.LifeInsurance,
  })

  return {
    allLifeInsuranceOffers,
    browseViewLifeInsuranceOffers,
    configuredStep: state.config.step as LifeInsuranceStep,
    companyUuid: configCompanyUuidSelector(state) ?? '',
    globalError: loadingErrorSelector(state, Product.LifeInsurance),
    loading: loadingSelector(state, Product.LifeInsurance),
    isLeapLifeBranded: isLeapLifeBrandedSelector(state),
    isOfferLoaded: state.ui?.[Product.LifeInsurance]?.isSelectedOfferLoaded,
    offerUuid: state.config[ConfigKeys.Offer],
    rateTableLeadUuid: lifeInsuranceRatesLeadUuidSelector(state),
    rateTableUuid: lifeInsuranceRatesUuidSelector(state),
    recommendedViewLifeInsuranceOffers: lifeInsuranceRecommendedViewRatesOffersSelector(state),
    resultsView: uiLifeInsuranceResultsViewSelector(state),
    applicationEmail: lifeInsuranceApplicationEmailFieldSelector(state),
    coverageAmount: uiLifeInsuranceCoverageAmountSelector(state),
    coverageDuration: uiLifeInsuranceCoverageDurationSelector(state),
    enableCustomBackground: configShouldUseCustomBackgroundSelector(state),
  }
}

const mapDispatchToProps = (dispatch: MultiProductDispatch): DispatchProps => ({
  dispatchAppendRateTableUuidToParentUrl: (uuid: string) => dispatch(actions.appendUuidToParentUrl(uuid)),
  dispatchRatesFilterChanged: (ratesFilter: RatesFilter, value: LifeInsuranceCoverageTier) =>
    dispatch(ratesFilterReplaced(Product.LifeInsurance, ratesFilter, value)),
  dispatchSetResultsView: (resultsView: LifeInsuranceResultsView) =>
    dispatch(actions.setLifeInsuranceResultsView(resultsView)),
  dispatchUpdateLead: () => dispatch(updateLead(Product.LifeInsurance)),
  dispatchReportProductSubType: (productSubType: LifeInsuranceProductSubType) =>
    dispatch(lifeInsuranceReportProductSubType(productSubType)),
  dispatchStepSelected: (product: Product, step: ProductStep) => dispatch(stepSelected(product, step)),
  dispatchLifeInsuranceOfferLoaded: () => dispatch(actions.lifeInsuranceOfferLoaded()),
  dispatchApplicationFieldShowValidationStatus: (fieldKey: FieldKey, show: boolean) =>
    dispatch(applicationFieldShowValidationStatus(Product.LifeInsurance, fieldKey, show)),
  dispatchFetchRateTablesSuccess: (product: Product, data) => dispatch(fetchRateTablesSuccess(product, data)),
  dispatchCoverageAmount: (coverageAmount: number) => dispatch(actions.setLifeInsuranceCoverageAmount(coverageAmount)),
  dispatchCoverageDuration: (coverageDuration: number) =>
    dispatch(actions.setLifeInsuranceCoverageDuration(coverageDuration)),
  onOfferSelected: (offer) => dispatch(lifeInsuranceOfferSelected(offer)),
})

export const LifeInsuranceResultsContainer = connect<StoreProps, DispatchProps>(
  mapStateToProps,
  mapDispatchToProps,
)(UnconnectedLifeInsuranceResultsContainer)
